<template>
  <div>
    <b-table
      ref="refUserListTable"
      responsive="md"
      class="position-relative"
      :items="orders"
      :fields="tableColumns"
      primary-key="id"
      show-empty
      :empty-text="loading ? $t('shared.loading') : $t('shared.no-data') "
      :sort-by.sync="query.sortBy"
      :sort-desc.sync="query.isSortDirDesc"
    >

      <!-- Column: typeColor -->
      <template #cell(typeColor)="data">
        <div
          v-if="['cancelled', 'draft'].includes(data.item.status)"
          class="w-100 h-100 bg-transparent"
        />
        <div
          v-else-if="['specialized_transport'].includes(data.item.type)"
          class="w-100 h-100 bg-danger"
        />
        <div
          v-else-if="['basic_transport', 'sanitary_transport'].includes(data.item.type)"
          class="w-100 h-100 bg-success"
        />
        <div
          v-else-if="['medical_courier'].includes(data.item.type)"
          class="w-100 h-100 bg-info"
        />
      </template>

      <!-- Column: fullNumber -->
      <template #cell(fullNumber)="data">
        <div class="text-nowrap text-center">

          <b-link
            v-if="data.item.fullNumber"
            :to="{ name: 'order-view', params: { id: data.item.id } }"
            class="font-medium-3"
            :class="{'line-through': data.item.status === 'cancelled'}"
          >
            {{ data.item.fullNumber }}
          </b-link>
          <b-link
            v-else-if="data.item.type"
            :to="{ name: 'order-edit', params: { id: data.item.id } }"
            class="font-medium-3"
          >
            {{ $t('order.types.' + data.item.type) }}
          </b-link>
          <br>
          <b-badge
            variant="light-secondary"
            class="font-medium-1"
            :style="{'background-color': getStatusColor(data.item.status)}"
          >
            {{ $t('order.status.' + data.item.status) }}
          </b-badge>
        </div>

      </template>

      <!-- Column: icons -->
      <template #cell(icons)="data">

        <div class="text-nowrap">
          <b-avatar
            v-if="data.item.prioritySignals"
            v-b-tooltip.hover.top="$t('order.priority-signals.true')"
            size="30"
            variant="primary"
            class="m-25"
          >
            <feather-icon
              icon="LoaderIcon"
              class="font-medium-5"
            />
          </b-avatar>
          <b-avatar
            v-if="data.item.absolutePunctuality"
            v-b-tooltip.hover.top="$t('order.label.base-order-info.absolute-punctuality')"
            size="30"
            variant="danger"
            class="m-25"
          >
            <feather-icon
              icon="ClockIcon"
              class="font-medium-5"
            />
          </b-avatar>
          <b-avatar
            v-if="data.item.infectious"
            v-b-tooltip.hover.top="$t('order.label.infectious')"
            size="30"
            variant="warning"
            class="m-25"
          >
            <feather-icon
              icon="AlertTriangleIcon"
              class="font-medium-5"
            />
          </b-avatar>
          <b-avatar
            v-if="data.item.private"
            v-b-tooltip.hover.top="$t('order.label.private-order')"
            size="30"
            variant="success"
            class="m-25"
          >
            <feather-icon
              icon="DollarSignIcon"
              class="font-medium-5"
            />
          </b-avatar>
          <b-avatar
            v-if="data.item.returnTransport"
            v-b-tooltip.hover.top="$t('order.label.base-order-info.return-transport')"
            size="30"
            class="m-25"
          >
            <feather-icon
              icon="RepeatIcon"
              class="font-medium-5"
            />
          </b-avatar>
        </div>

      </template>

      <!-- Column: pickupAddress -->
      <template #cell(pickupAddress)="data">

        <div v-if="data.item.pickupAddress">
          <span v-if="data.item.pickupAddress.alias">
            {{ data.item.pickupAddress.alias }}<br>
            <small>{{ data.item.pickupAddress.name }}</small>
          </span>
          <span v-else-if="data.item.pickupAddress.id">
            <span v-if="data.item.pickupAddress.name">{{ data.item.pickupAddress.name }}<br></span>
            <small>{{ data.item.pickupAddress.city }}, {{ data.item.pickupAddress.address }}</small>
          </span>
        </div>

      </template>

      <!-- Column: deliveryAddress -->
      <template #cell(deliveryAddress)="data">

        <div v-if="data.item.deliveryAddress">
          <span v-if="data.item.deliveryAddress.alias">
            {{ data.item.deliveryAddress.alias }}<br>
            <small>{{ data.item.deliveryAddress.name }}</small>
          </span>
          <span v-else-if="data.item.deliveryAddress.id">
            <span v-if="data.item.deliveryAddress.name">{{ data.item.deliveryAddress.name }}<br></span>
            <small>{{ data.item.deliveryAddress.city }}, {{ data.item.deliveryAddress.address }}</small>
          </span>
        </div>

      </template>

      <!-- Column: team -->
      <template #cell(team)="data">

        <b-avatar
          v-if="data.item.isFirstAvailablePlanning && Array.isArray(data.item.plannedTeamDefinitions) && data.item.plannedTeamDefinitions.length"
          v-b-tooltip.hover.top="data.item.plannedTeamDefinitions.map(td => td.code).join(', ')"
          size="30"
          variant="light-warning"
          class="m-25"
        >
          <feather-icon
            icon="UsersIcon"
            class="font-medium-5"
          />
        </b-avatar>
        <b-badge
          v-else-if="getTeamDefinition(data.item)"
          class="text-nowrap font-medium-1"
          :variant="isPlannedTeam(data.item) ? 'light-secondary' : 'light-primary'"
        >
          <feather-icon
            v-if="isPlannedTeam(data.item)"
            icon="ClockIcon"
            class="font-medium-1"
          />
          {{ getTeamDefinition(data.item) }}
        </b-badge>

      </template>

      <!-- Column: startAt -->
      <template #cell(startAt)="data">

        <div class="">
          <b-badge
            class="text-nowrap font-medium-1"
            variant="light-secondary"
          >
            {{ transformDatetimeToHumanReadablePretty(data.item.startAt) }}
          </b-badge>
          <timer
            :time="data.item.startAt"
            :show="data.item.status === 'received'"
          />
        </div>

      </template>

      <!-- Column: teamEndedAt -->
      <template #cell(teamEndedAt)="data">

        <div
          v-if="data.item.teamEndedAt"
          class=""
        >
          {{ transformDatetimeToHumanReadablePretty }}
        </div>

      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">

        <div class="d-flex justify-content-center">
          <b-dropdown
            v-if="hasAnyActions(data.item)"
            right
            size="sm"
            variant="flat-secondary"
            no-caret
            toggle-class="btn btn-flat-primary btn-sm p-50"
            class="order-actions-dropdown"
          >
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" />
            </template>

            <b-dropdown-item
              v-if="canShowViewAction(data.item)"
              :to="{ name: 'order-view', params: { id: data.item.id } }"
            >
              <feather-icon
                icon="EyeIcon"
                size="14"
                class="mr-50"
              />
              <span>{{ $t('shared.button.see') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="isEditButtonVisible(data.item)"
              :to="{ name: 'order-edit', params: { id: data.item.id } }"
            >
              <feather-icon
                icon="EditIcon"
                size="14"
                class="mr-50"
              />
              <span>{{ $t('shared.edit') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="canManagePlanning(data.item)"
              @click.prevent="openPlanningModal(data.item)"
            >
              <feather-icon
                icon="ClockIcon"
                size="14"
                class="mr-50"
              />
              <span>{{ getPlanningActionLabel(data.item) }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="canPrintKmcr(data.item)"
              :to="getPrintKmcrRoute(data.item)"
              target="_blank"
              rel="noopener"
            >
              <feather-icon
                icon="PrinterIcon"
                size="14"
                class="mr-50"
              />
              <span>{{ $t('order.actions.print-kmcr') }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="canCloneOrders"
              :disabled="isCloningOrder(data.item.id)"
              @click.prevent="handleCloneOrder(data.item)"
            >
              <b-spinner
                v-if="isCloningOrder(data.item.id)"
                small
                class="mr-50"
              />
              <feather-icon
                v-else
                icon="CopyIcon"
                size="14"
                class="mr-50"
              />
              <span>{{ $t('order.actions.clone') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>

      </template>

    </b-table>

    <order-planning-modal
      ref="orderPlanningModal"
      @planning-updated="handlePlanningUpdated"
    />
  </div>
</template>
<script>
import { parseRequestError, transformDatetimeToHumanReadablePretty } from '@/helpers/helpers'
import canEditOrder from '@/helpers/order'
import {
  BAvatar,
  BBadge,
  BDropdown,
  BDropdownItem,
  BLink,
  BSpinner,
  BTable,
  VBTooltip,
} from 'bootstrap-vue'
import Timer from '@/views/orders/partials/Timer.vue'
import OrderPlanningModal from '@/views/orders/OrderPlanning/OrderPlanningModal.vue'

const KMCR_PRINTABLE_ORDER_STATUSES = ['completed', 'done']

export default {
  components: {
    BTable,
    BLink,
    BBadge,
    BAvatar,
    BDropdown,
    BDropdownItem,
    BSpinner,

    OrderPlanningModal,
    Timer,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    loading: {
      type: Boolean,
      default: true,
    },
    orders: {
      type: Array,
      default: () => [],
    },
    query: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      cloningOrderId: null,
      tableColumns: [
        {
          label: '',
          key: 'typeColor',
          class: 'order-type-color-column position-relative d-none d-md-table-cell p-50',
        },
        {
          label: this.$t('order.label.full-number'),
          key: 'fullNumber',
          sortable: true,
          class: 'p-50',
        },
        {
          label: '',
          key: 'icons',
          class: 'text-center p-50',
        },
        {
          label: this.$t('order.label.pickup-address'),
          key: 'pickupAddress',
          sortable: false,
          class: 'd-none d-lg-table-cell p-50',
        },
        {
          label: this.$t('order.label.delivery-address'),
          key: 'deliveryAddress',
          sortable: false,
          class: 'd-none d-lg-table-cell p-50',
        },
        {
          label: '',
          key: 'team',
          class: 'text-center p-50',
        },
        {
          label: this.$t('order.label.start-date'),
          key: 'startAt',
          sortable: true,
          class: 'text-center p-50',
        },
        {
          label: this.$t('shared.actions'),
          key: 'actions',
          value: null,
          class: 'text-center p-50',
        },
      ],
    }
  },
  computed: {
    me() {
      return this.$store.state.auth.activeUser
    },
    canCloneOrders() {
      return this.me && Array.isArray(this.me.roles) && this.me.roles.includes('ROLE_DISPATCHER')
    },
  },
  methods: {
    transformDatetimeToHumanReadablePretty,
    getTeamDefinition(order) {
      if (order && order.team && order.team.teamDefinition && order.team.teamDefinition.code) {
        return order.team.teamDefinition.code
      }
      if (order && order.plannedTeamDefinition && order.plannedTeamDefinition.code) {
        return order.plannedTeamDefinition.code
      }
      return null
    },
    isPlannedTeam(order) {
      return !(order && order.team && order.team.teamDefinition && order.team.teamDefinition.code)
    },
    getStatusColor(status) {
      switch (status) {
        case 'received':
          return '#F7E7C4'
        case 'cancelled':
          return '#C6DAFB'
        default:
          return null
      }
    },
    isEditButtonVisible(order) {
      return canEditOrder(order, this.me.roles)
    },
    canShowViewAction(order) {
      return order && order.status !== 'draft'
    },
    hasAnyActions(order) {
      return this.canShowViewAction(order) || this.isEditButtonVisible(order) || this.canManagePlanning(order) || this.canPrintKmcr(order) || this.canCloneOrders
    },
    canPrintKmcr(order) {
      return !!(order && order.id && KMCR_PRINTABLE_ORDER_STATUSES.includes(order.status))
    },
    getPrintKmcrRoute(order) {
      return {
        name: 'order-order',
        params: { id: order.id },
        query: { autoPrint: '1' },
      }
    },
    canManagePlanning(order) {
      return !!(
        this.canCloneOrders
        && order
        && order.id
        && order.status === 'received'
        && !(order.team && order.team.id)
      )
    },
    getPlanningActionLabel(order) {
      return (order?.plannedTeamDefinition || order?.isFirstAvailablePlanning)
        ? this.$t('order.planning.edit-action')
        : this.$t('order.planning.plan-action')
    },
    openPlanningModal(order) {
      if (!this.canManagePlanning(order) || !this.$refs.orderPlanningModal) {
        return
      }

      this.$refs.orderPlanningModal.openForOrder(order)
    },
    handlePlanningUpdated() {
      this.$emit('planning-updated')
    },
    isCloningOrder(orderId) {
      return this.cloningOrderId === orderId
    },
    handleCloneOrder(order) {
      if (!this.canCloneOrders || !order || !order.id || this.isCloningOrder(order.id)) {
        return
      }
      this.cloningOrderId = order.id
      this.$store.dispatch('order/cloneOrder', order.id)
        .then(response => {
          window.toast.notify.success(this.$t('order.notify.clone.success_title'))
          const clonedOrderId = response && response.data ? response.data.id : null
          if (clonedOrderId) {
            this.$router.push({ name: 'order-edit', params: { id: clonedOrderId } })
          }
        })
        .catch(err => {
          window.toast.notify.danger(this.$t('order.notify.clone.error_title'))
          parseRequestError(this.stripErrorMessage(err))
        })
        .finally(() => {
          this.cloningOrderId = null
        })
    },
    stripErrorMessage(err) {
      if (err && err.response && err.response.data && err.response.data.message) {
        return {
          ...err,
          response: {
            ...err.response,
            data: {
              ...err.response.data,
              message: null,
            },
          },
        }
      }
      return err
    },
  },
}
</script>

<style lang="scss">
.order-type-color-column {
  width: 8px !important;
  min-width: 8px;
  padding: 0 !important;

  > div {
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
  }
}
</style>
